import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";

export default function BillingSuccess() {
  const [state, setState] = useState("checking"); // checking | success | failed
  const [planId, setPlanId] = useState("");
  const navigate = useNavigate();

  const poll = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setState("failed"); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") { setPlanId(data.plan_id); setState("success"); return; }
      if (data.status === "expired") { setState("failed"); return; }
    } catch (error) {
      console.error("Payment status check failed, retrying:", error);
    }
    setTimeout(() => poll(sessionId, attempt + 1), 2000);
  }, []);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState("failed"); return; }
    poll(sid);
  }, [poll]);

  return (
    <div className="min-h-screen text-white">
      <Navbar />
      <main className="pt-32 pb-20 max-w-lg mx-auto px-6 text-center">
        {state === "checking" && (
          <div className="flex flex-col items-center gap-4 text-slate-300" data-testid="billing-checking">
            <Loader2 className="animate-spin text-cyanbrand" size={44} />
            <p>Confirming your payment…</p>
          </div>
        )}
        {state === "success" && (
          <div className="flex flex-col items-center gap-4" data-testid="billing-success">
            <CheckCircle2 className="text-green-400" size={56} />
            <h1 className="font-heading text-3xl font-semibold">You're all set! 🎉</h1>
            <p className="text-slate-400 capitalize">Your {planId} plan is now active. All features are unlocked.</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-4 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold px-6 py-3 rounded-md transition-all"
              style={{ boxShadow: "0 0 15px rgba(0,229,255,0.3)" }}
            >
              Go to dashboard
            </button>
          </div>
        )}
        {state === "failed" && (
          <div className="flex flex-col items-center gap-4" data-testid="billing-failed">
            <XCircle className="text-red-400" size={56} />
            <h1 className="font-heading text-2xl font-semibold">Payment not confirmed</h1>
            <p className="text-slate-400">We couldn't confirm your payment. If you were charged, it'll reflect shortly.</p>
            <button
              onClick={() => navigate("/billing")}
              className="mt-4 border border-slate-700 hover:border-cyanbrand text-white px-6 py-3 rounded-md transition-colors"
            >
              Back to billing
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
